import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import type { ArchitectureLayer } from '../types';

const ArchitectureSection: React.FC = () => {
  const [activeLayer, setActiveLayer] = useState<string>('frontend');

  const architectureLayers: ArchitectureLayer[] = [
    {
      id: 'frontend',
      name: 'Frontend Layer',
      description: 'React 18 with TypeScript, server-side rendering for optimal performance and SEO',
      technologies: ['React 18', 'Next.js', 'TypeScript', 'Redux Toolkit', 'Styled Components'],
      icon: 'Layout'
    },
    {
      id: 'api',
      name: 'API Gateway',
      description: 'RESTful APIs with JWT authentication and rate limiting for secure access',
      technologies: ['REST API', 'JWT Auth', 'Rate Limiting', 'API Versioning', 'GraphQL'],
      icon: 'Network'
    },
    {
      id: 'ai',
      name: 'AI Processing',
      description: 'Advanced computer vision models with real-time object detection and tracking',
      technologies: ['TensorFlow', 'PyTorch', 'OpenCV', 'YOLO v8', 'Custom Models'],
      icon: 'Brain'
    },
    {
      id: 'data',
      name: 'Data Layer',
      description: 'Scalable database architecture with real-time analytics and data warehousing',
      technologies: ['PostgreSQL', 'Redis', 'Elasticsearch', 'MongoDB', 'TimescaleDB'],
      icon: 'Database'
    },
    {
      id: 'infrastructure',
      name: 'Infrastructure',
      description: 'Containerized deployment with auto-scaling and global CDN distribution',
      technologies: ['Docker', 'Kubernetes', 'AWS', 'CDN', 'Load Balancing'],
      icon: 'Server'
    }
  ];

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Technical Architecture
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Built on modern, scalable infrastructure designed for enterprise performance and reliability
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1 space-y-3">
            {architectureLayers.map((layer) => (
              <button
                key={layer.id}
                onClick={() => setActiveLayer(layer.id)}
                className={`w-full text-left p-4 rounded-lg transition-all duration-300 ${
                  activeLayer === layer.id
                    ? 'bg-primary text-primary-foreground shadow-brand-lg'
                    : 'bg-card hover:bg-muted text-foreground'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Icon 
                    name={layer.icon} 
                    size={24} 
                    className={activeLayer === layer.id ? 'text-primary-foreground' : 'text-primary'}
                  />
                  <span className="font-semibold">{layer.name}</span>
                </div>
              </button>
            ))}
          </div>

          <div className="lg:col-span-2">
            {architectureLayers.map((layer) => (
              <div
                key={layer.id}
                className={`${
                  activeLayer === layer.id ? 'block' : 'hidden'
                } bg-card rounded-lg p-8 shadow-brand`}
              >
                <div className="flex items-start gap-4 mb-6">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <Icon name={layer.icon} size={32} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-foreground mb-2">{layer.name}</h3>
                    <p className="text-text-secondary">{layer.description}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-semibold text-foreground">Key Technologies:</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {layer.technologies.map((tech, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 p-3 bg-background rounded-lg border border-border"
                      >
                        <Icon name="Check" size={16} className="text-success" />
                        <span className="text-sm font-medium text-foreground">{tech}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-6 p-4 bg-primary/5 rounded-lg border border-primary/20">
                  <div className="flex items-start gap-3">
                    <Icon name="Info" size={20} className="text-primary mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground mb-1">Performance Optimized</p>
                      <p className="text-sm text-text-secondary">
                        This layer is optimized for high throughput and low latency with automatic scaling capabilities.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ArchitectureSection;